// Landing screen for the OAuth return: #/connected/<state>.
// The callback already exchanged and stored the grant; this screen reads the
// flow's results and, when Google or Meta returned more than one entity,
// renders the picker that finishes the job.
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { api, ApiError, type ConnectStatus } from "../api";
import { go } from "../router";
import { StatusChip } from "../components/StatusChip";
import { track } from "../lib/analytics";
import { onboardingInProgress } from "../components/connect/forms";
import { rememberProduct } from "../lib/activeProduct";

export function ConnectDone({ state }: { state: string }) {
  const [provider, setProvider] = useState<"google" | "meta">("google");
  const [status, setStatus] = useState<ConnectStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Google selections
  const [ga4Choice, setGa4Choice] = useState("");
  const [gtmChoice, setGtmChoice] = useState("");
  const [adsChoice, setAdsChoice] = useState("");
  const [gscChoice, setGscChoice] = useState("");

  // Meta selections
  const [metaAdAccountChoice, setMetaAdAccountChoice] = useState("");
  const [metaPageChoice, setMetaPageChoice] = useState("");

  const [busy, setBusy] = useState(false);
  const [activated, setActivated] = useState<string[] | null>(null);

  useEffect(() => {
    api.connect.status(state)
      .then((s) => {
        setProvider("google");
        setStatus(s);
        if (s.product_slug) rememberProduct(s.product_slug);
      })
      .catch(async () => {
        try {
          const ms = await api.connectMeta.status(state);
          setProvider("meta");
          setStatus(ms);
          if (ms.product_slug) rememberProduct(ms.product_slug);
        } catch (e) {
          setLoadError(e instanceof ApiError ? e.message : String(e));
        }
      });
  }, [state]);

  if (loadError) return <Message title="Connection not found" body={loadError} />;
  if (!status) return <Message title="Checking…" body="Reading the connection result." />;
  if (status.status === "pending") {
    return <Message title="Still authorizing" body={`Finish the ${provider === "meta" ? "Meta" : "Google"} consent screen, then reload this page.`} />;
  }
  if (status.status === "error") return <Message title="Connection failed" body={status.error} />;

  const opts = status.options ?? {};
  const isMeta = provider === "meta" || status.results.some((r) => r.kind.startsWith("meta_"));

  // Google options
  const props = opts.ga4_properties ?? [];
  const containers = opts.gtm_containers ?? [];
  const adsAccounts = (opts.ads_customers ?? []).filter((c) => !c.manager);
  const gscSites = opts.gsc_sites ?? [];

  // Meta options
  const metaAdAccounts = opts.meta_ad_accounts ?? [];
  const metaPages = opts.meta_pages ?? [];

  const kinds = status.results.map((r) => r.kind);
  const needsGa4 = kinds.includes("ga4");
  const needsGtm = kinds.includes("gtm");
  const needsAds = kinds.includes("google_ads");
  const needsGsc = kinds.includes("gsc");
  const needsMetaAds = kinds.includes("meta_ads");
  const needsMetaSocial = kinds.includes("meta_social");

  const ga4Pick = needsGa4 ? ga4Choice || (props.length === 1 ? props[0].id : "") : "";
  const gtmPick = needsGtm ? gtmChoice || (containers.length === 1 ? containers[0].path : "") : "";
  const adsPick = needsAds ? adsChoice || (adsAccounts.length === 1 ? adsAccounts[0].id : "") : "";
  const gscPick = needsGsc ? gscChoice || (gscSites.length === 1 ? gscSites[0].site_url : "") : "";

  const metaAdsPick = needsMetaAds ? metaAdAccountChoice || (metaAdAccounts.length === 1 ? metaAdAccounts[0].id : "") : "";
  const metaPagePick = needsMetaSocial ? metaPageChoice || (metaPages.length === 1 ? metaPages[0].id : "") : "";

  const nothingPicked = isMeta
    ? (!metaAdsPick && !metaPagePick)
    : (!ga4Pick && !gtmPick && !adsPick && !gscPick);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (isMeta) {
        const res = await api.connectMeta.select({
          state,
          ad_account_id: metaAdsPick || undefined,
          page_id: metaPagePick || undefined,
        });
        track("source_connected", { kinds: res.activated.join(",") });
        setActivated(res.activated);
      } else {
        const res = await api.connect.select({
          state,
          ga4_property_id: ga4Pick || undefined,
          gtm_container_path: gtmPick || undefined,
          ads_customer_id: adsPick || undefined,
          gsc_site_url: gscPick || undefined,
        });
        track("source_connected", { kinds: res.activated.join(",") });
        setActivated(res.activated);
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (activated) {
    return (
      <Message
        title="Connected"
        body={`${activated.join(", ")} now feeding the warehouse. We're reading your history now — the first weeks land in about a minute and the dashboard fills in as they do.`}
        action={<button className="btn-solid" onClick={() => go(onboardingInProgress()
          ? { name: "product", slug: null, section: "dashboard" }
          : { name: "integrations", kind: activated[0] })}>
          {onboardingInProgress() ? "Back to setup" : "Back to integrations"}
        </button>}
      />
    );
  }

  return (
    <div className="page" style={{ paddingTop: 48, maxWidth: 640 }}>
      <h1>{isMeta ? "Meta connected" : "Google connected"}</h1>
      <span className="sub">
        {opts.discovery_error
          ? `Connected, but listing your assets failed: ${opts.discovery_error}`
          : "Choose what to track."}
      </span>

      <div className="panel" style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 18 }}>
        {status.results.map((r) => (
          <div key={r.kind} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontWeight: 500, width: 120 }}>{r.kind}</span>
            <StatusChip kind={r.status === "active" ? "passing" : "pre-gate"} label={r.status} />
          </div>
        ))}

        {/* Google Fields */}
        {!isMeta && needsGa4 && props.length > 0 && (
          <div className="field">
            <label htmlFor="ga4-pick">GA4 property</label>
            <select id="ga4-pick" className="input" value={ga4Choice || (props.length === 1 ? props[0].id : "")}
              onChange={(e) => setGa4Choice(e.target.value)}>
              {props.length > 1 && <option value="">Choose…</option>}
              {props.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.id})</option>)}
            </select>
          </div>
        )}
        {!isMeta && needsGtm && containers.length > 0 && (
          <div className="field">
            <label htmlFor="gtm-pick">GTM container</label>
            <select id="gtm-pick" className="input" value={gtmChoice || (containers.length === 1 ? containers[0].path : "")}
              onChange={(e) => setGtmChoice(e.target.value)}>
              {containers.length > 1 && <option value="">Choose…</option>}
              {containers.map((c) => <option key={c.path} value={c.path}>{c.name} ({c.public_id})</option>)}
            </select>
          </div>
        )}
        {!isMeta && needsAds && adsAccounts.length > 0 && (
          <div className="field">
            <label htmlFor="ads-pick">Google Ads account</label>
            <select id="ads-pick" className="input" value={adsChoice || (adsAccounts.length === 1 ? adsAccounts[0].id : "")}
              onChange={(e) => setAdsChoice(e.target.value)}>
              {adsAccounts.length > 1 && <option value="">Choose…</option>}
              {adsAccounts.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.currency})</option>)}
            </select>
          </div>
        )}
        {!isMeta && needsGsc && gscSites.length > 0 && (
          <div className="field">
            <label htmlFor="gsc-pick">Search Console site</label>
            <select id="gsc-pick" className="input" value={gscChoice || (gscSites.length === 1 ? gscSites[0].site_url : "")}
              onChange={(e) => setGscChoice(e.target.value)}>
              {gscSites.length > 1 && <option value="">Choose…</option>}
              {gscSites.map((s) => <option key={s.site_url} value={s.site_url}>{s.site_url}</option>)}
            </select>
          </div>
        )}
        {!isMeta && needsGsc && gscSites.length === 0 && (
          <div className="field">
            <label htmlFor="gsc-id">Search Console site URL</label>
            <input id="gsc-id" className="input" placeholder="https://example.com/ or sc-domain:example.com"
              value={gscChoice} onChange={(e) => setGscChoice(e.target.value)} />
            <span className="form-note">
              {opts.gsc_error
                ? `We couldn't list your sites: ${opts.gsc_error}. Enter the URL as it appears in Search Console.`
                : "Enter the property URL exactly as it appears in Search Console."}
            </span>
          </div>
        )}
        {!isMeta && needsAds && adsAccounts.length === 0 && (
          <div className="field">
            <label htmlFor="ads-id">Google Ads customer id</label>
            <input id="ads-id" className="input" inputMode="numeric" placeholder="123-456-7890"
              value={adsChoice} onChange={(e) => setAdsChoice(e.target.value)} />
            <span className="form-note">
              {opts.ads_blocked === "discovery_failed"
                ? `We couldn't list your Ads accounts: ${opts.ads_error ?? "unknown error"}. Enter the id manually.`
                : opts.ads_blocked
                  ? "Find it in the top corner of ads.google.com. Spend sync starts once FunnelKeeper's Google Ads API access covers live accounts — your id is stored now."
                  : "Find it in the top corner of ads.google.com."}
            </span>
          </div>
        )}

        {/* Meta Fields */}
        {isMeta && needsMetaAds && metaAdAccounts.length > 0 && (
          <div className="field">
            <label htmlFor="meta-ads-pick">Meta Ad Account</label>
            <select id="meta-ads-pick" className="input" value={metaAdAccountChoice || (metaAdAccounts.length === 1 ? metaAdAccounts[0].id : "")}
              onChange={(e) => setMetaAdAccountChoice(e.target.value)}>
              {metaAdAccounts.length > 1 && <option value="">Choose…</option>}
              {metaAdAccounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}
            </select>
          </div>
        )}
        {isMeta && needsMetaAds && metaAdAccounts.length === 0 && (
          <div className="field">
            <label htmlFor="meta-ads-id">Meta Ad Account ID</label>
            <input id="meta-ads-id" className="input" placeholder="Numeric ad account id (e.g. 123456789)"
              value={metaAdAccountChoice} onChange={(e) => setMetaAdAccountChoice(e.target.value)} />
            <span className="form-note">
              Enter your numeric Meta ad account ID (without act_ prefix).
            </span>
          </div>
        )}

        {isMeta && needsMetaSocial && metaPages.length > 0 && (
          <div className="field">
            <label htmlFor="meta-page-pick">Facebook Page & Instagram</label>
            <select id="meta-page-pick" className="input" value={metaPageChoice || (metaPages.length === 1 ? metaPages[0].id : "")}
              onChange={(e) => setMetaPageChoice(e.target.value)}>
              {metaPages.length > 1 && <option value="">Choose…</option>}
              {metaPages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.instagram_name ? `${p.name} (@${p.instagram_name})` : p.name}
                </option>
              ))}
            </select>
          </div>
        )}
        {isMeta && needsMetaSocial && metaPages.length === 0 && (
          <div className="field">
            <label htmlFor="meta-page-id">Facebook Page ID</label>
            <input id="meta-page-id" className="input" placeholder="Facebook Page ID"
              value={metaPageChoice} onChange={(e) => setMetaPageChoice(e.target.value)} />
            <span className="form-note">
              Enter the numeric Facebook Page ID to track.
            </span>
          </div>
        )}

        {error && <div className="form-error">{error}</div>}
        <div>
          <button className="btn-solid" onClick={submit} disabled={busy || nothingPicked}>
            {busy ? "Saving…" : "Finish"}
          </button>
          {nothingPicked && (
            <span className="form-note" style={{ marginLeft: 12 }}>
              Choose what to track first.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function Message({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <div className="page" style={{ paddingTop: 48, maxWidth: 560 }}>
      <div className="panel" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <h2>{title}</h2>
        <p style={{ fontSize: 13.5, lineHeight: 1.5, color: "var(--text-3)", margin: 0 }}>{body}</p>
        {action && <div style={{ marginTop: 8 }}>{action}</div>}
      </div>
    </div>
  );
}
